import { Injectable } from "@angular/core";
import { defer, fromEvent, merge, Observable, of, throwError } from "rxjs";
import { concatMap, map, mergeMap } from "rxjs/operators";

@Injectable()
export class DeviceService {
  devices$(): Observable<MediaDeviceInfo[]> {
    return defer(() => this.unlockDeviceLabels()).pipe(
      concatMap(() =>
        merge(of(null), fromEvent(navigator.mediaDevices, "devicechange"))
      ),
      concatMap(() => navigator.mediaDevices.enumerateDevices()),
      map((_) =>
        // Ignore pseudo ids.
        // It may cause un-expected behaviour during the device change, e.g.
        // device was changed by deviceId is the same - 'default'.
        _.filter((_) => !["default", "communications"].includes(_.deviceId))
      )
    );
  }

  // Browsers only expose device labels once the page holds a capture permission, so a
  // short camera+mic capture is opened to obtain it. This probe owns its tracks and stops
  // them before returning: it must not stay captured for the whole session (and Safari
  // warns when a capture track is garbage-collected without stop()). Labels stay
  // available after stop() in both Chromium and WebKit.
  private async unlockDeviceLabels(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const labelsHidden = devices.some(
      (_) => (_.kind === "audioinput" || _.kind === "videoinput") && !_.label
    );
    if (!labelsHidden) {
      return;
    }

    const probe = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "user" }
      },
      audio: true
    });
    probe.getTracks().forEach((_) => _.stop());
  }

  getDisplayMedia$(): Observable<MediaStream> {
    return defer(() =>
      navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1_280 },
          height: { ideal: 720 }
        },
        audio: true
      })
    ).pipe(
      mergeMap(async (_) => {
        const videos = _.getVideoTracks();

        for (const video of videos) {
          await video.applyConstraints({
            width: { ideal: 1_280 },
            height: { ideal: 720 },
            // Set min frameRate to 15 (default value is 0),
            // to reduce display media delay appearence on the Mixer side from 30s to normal 5s.
            frameRate: { min: 15, max: 30, ideal: 30 }
          });
        }

        return _;
      })
    );
  }

  getUserMedia$(constraints: MediaStreamConstraints): Observable<MediaStream> {
    return defer(() => navigator.mediaDevices.getUserMedia(constraints));
  }

  getUserMediaVideoInputStreamTrack$(
    constraints: MediaTrackConstraints
  ): Observable<MediaStreamTrack> {
    return defer(() =>
      navigator.mediaDevices.getUserMedia({
        video: constraints
      })
    ).pipe(
      map((_) => _.getVideoTracks()[0]),
      concatMap((_) => (_ ? of(_) : throwError("No videoInputStreamTrack")))
    );
  }

  getUserMediaAudioInputStreamTrack$(
    constraints: MediaTrackConstraints
  ): Observable<MediaStreamTrack> {
    return defer(() =>
      navigator.mediaDevices.getUserMedia({
        audio: constraints
      })
    ).pipe(
      map((_) => _.getAudioTracks()[0]),
      concatMap((_) => (_ ? of(_) : throwError("No audioInputStreamTrack")))
    );
  }
}
